import React, { useCallback, useEffect, useState } from "react";
import { Container } from "reactstrap";
import { useParams } from "react-router-dom";
import PageLoadingSpinner from "components/common/PageLoader";
import BreadCrumb from "components/common/BreadCrumb";
import { getDocumentTitle } from "utils/helpers";
import { getAmcVisitById } from "features/AmcVisitLogs/services/AmcVisitService";
import type { AmcVisit } from "features/AmcVisitLogs/types/amc-visit.types";
import AmcVisitOverviewContext from "./AmcVisitOverviewContext";
import Section from "./Section";

const AmcVisitOverview = () => {
  document.title = getDocumentTitle("Visit details");
  const { visitId: visitIdParam } = useParams<{ visitId: string }>();

  const [visit, setVisit] = useState<AmcVisit | null>(null);
  const [loading, setLoading] = useState(true);

  const refetch = useCallback(async () => {
    const vid = Number(visitIdParam);
    if (!Number.isInteger(vid) || vid <= 0) {
      setVisit(null);
      return;
    }
    try {
      const res = await getAmcVisitById(vid);
      if (res.success && res.data) {
        const raw = res.data as any;
        // Normalize status_history → statusHistory so all consumers can read one field
        if (!raw.statusHistory && raw.status_history) {
          raw.statusHistory = raw.status_history;
        }
        setVisit(raw);
      } else {
        setVisit(null);
      }
    } catch (err) {
      console.warn(err);
      setVisit(null);
    }
  }, [visitIdParam]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      await refetch();
      if (!cancelled) setLoading(false);
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [refetch]);

  const visitLogsListPath =
    visit != null
      ? `/amc-management/visit-logs?amcId=${visit.amcId}`
      : "/amc-management/visit-logs";

  if (loading) {
    return <PageLoadingSpinner message="Loading visit…" />;
  }

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title="Visit details"
          pageTitle="AMC Management"
          items={[{ label: "Visit logs", path: visitLogsListPath }]}
        />
        <AmcVisitOverviewContext.Provider value={{ visit, refetch }}>
          <Section />
        </AmcVisitOverviewContext.Provider>
      </Container>
    </div>
  );
};

export default AmcVisitOverview;
